import { useEffect, useRef, useState } from "react";
import { gsap, prefersReducedMotion } from "../../lib/motion";
import "./Cursor.css";

/** A real mouse — not "big screen". A narrow desktop window still has one. */
const hasFinePointer = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(hover: hover) and (pointer: fine)").matches;

/**
 * Two-part cursor: a hard dot that tracks 1:1 and a soft ring that lags.
 * Elements marked `data-cursor="hover"` inflate the ring; `data-cursor="drag"`
 * swaps it for a drag affordance.
 *
 * The native cursor is only hidden once this one is actually tracking — the
 * `has-custom-cursor` class is added on the first real pointer move, so a
 * bail-out at any point leaves the visitor with their normal arrow.
 */
export default function Cursor() {
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const [enabled, setEnabled] = useState(false);

  /* 1 — decide whether to render at all */
  useEffect(() => {
    if (prefersReducedMotion() || !hasFinePointer()) return;
    setEnabled(true);
  }, []);

  /* 2 — wire the listeners *after* the nodes exist. Splitting this from the
     capability check matters: on the first pass `enabled` is false, the
     component renders null, and both refs are still null. */
  useEffect(() => {
    if (!enabled) return undefined;

    const dot = dotRef.current;
    const ring = ringRef.current;
    if (!dot || !ring) return undefined;

    const root = document.documentElement;
    const dotX = gsap.quickTo(dot, "x", { duration: 0.12, ease: "power3.out" });
    const dotY = gsap.quickTo(dot, "y", { duration: 0.12, ease: "power3.out" });
    const ringX = gsap.quickTo(ring, "x", { duration: 0.55, ease: "power3.out" });
    const ringY = gsap.quickTo(ring, "y", { duration: 0.55, ease: "power3.out" });

    let visible = false;

    const onMove = (e) => {
      if (!visible) {
        visible = true;
        root.classList.add("has-custom-cursor");
        gsap.set([dot, ring], { x: e.clientX, y: e.clientY });
        gsap.to([dot, ring], { autoAlpha: 1, duration: 0.3 });
      }
      dotX(e.clientX);
      dotY(e.clientY);
      ringX(e.clientX);
      ringY(e.clientY);
    };

    const onLeave = () => {
      visible = false;
      root.classList.remove("has-custom-cursor");
      gsap.to([dot, ring], { autoAlpha: 0, duration: 0.25 });
    };

    const onOver = (e) => {
      const target = e.target.closest?.("[data-cursor], a, button");
      const mode = target?.dataset?.cursor ?? (target ? "hover" : null);
      ring.dataset.mode = mode || "";
      gsap.to(ring, {
        scale: mode === "drag" ? 2.1 : mode ? 1.85 : 1,
        duration: 0.4,
        ease: "power3.out",
      });
      gsap.to(dot, { scale: mode ? 0 : 1, duration: 0.35, ease: "power3.out" });
    };

    const onDown = () => gsap.to(ring, { scale: 0.85, duration: 0.2 });
    const onUp = () => gsap.to(ring, { scale: ring.dataset.mode ? 1.85 : 1, duration: 0.3 });

    // A touch anywhere means this is a hybrid device — give the OS cursor back.
    const onTouch = () => {
      root.classList.remove("has-custom-cursor");
      gsap.set([dot, ring], { autoAlpha: 0 });
      visible = false;
    };

    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerover", onOver, { passive: true });
    window.addEventListener("pointerdown", onDown);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("touchstart", onTouch, { passive: true });
    document.addEventListener("mouseleave", onLeave);
    window.addEventListener("blur", onLeave);

    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerover", onOver);
      window.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("touchstart", onTouch);
      document.removeEventListener("mouseleave", onLeave);
      window.removeEventListener("blur", onLeave);
      root.classList.remove("has-custom-cursor");
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <div className="cursor" aria-hidden="true">
      <span className="cursor__ring" ref={ringRef} />
      <span className="cursor__dot" ref={dotRef} />
    </div>
  );
}
